import { useQuery } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import { fetchHome } from '~/api/home'
import type { GameItem } from '../types/game-content'
import { normalizeBannerLinkType, type HomeBannerItem } from '../types/banner'
import type {
  HomeCategoryColumns,
  HomeCategoryDisplayMode,
  HomeCategorySource,
  HomeCategoryTab,
} from '../types/game-tabs'
import type { JackpotTickerItem } from '../types/jackpot-ticker'
import type { HomeApiData, HomeApiJackpotEmptyMode } from '../types/home-aggregate'
import { mapApiGameToItem, mapApiVendorToSubTab } from '../utils/mapGameApi'
import { mapJackpotItems } from '../utils/mapJackpotItems'

export interface UseHomeAggregateOptions {
  sectionLimit?: number
  /** 默认 true；额度转换抽屉关闭时可不拉首页 */
  enabled?: MaybeRef<boolean>
}

export interface HomeAggregateViewModel {
  banners: HomeBannerItem[]
  /** 后端 categories，已按 sort 升序 */
  categories: HomeCategoryTab[]
  recommendedGames: GameItem[]
  /** 透传 home.jackpot_config.enabled */
  enabled: boolean
  /** 是否展示卡片左侧游戏封面；缺省展示 */
  showAvatar: boolean
  /** 横向滚动速度，像素/秒；缺省 40 */
  scrollSpeedPixelsPerSecond: number
  /** 无数据时 hide 隐藏模块，placeholder 展示占位；缺省 placeholder */
  emptyMode: HomeApiJackpotEmptyMode
  /** 后台占位文案；空则组件回退 i18n */
  emptyText: string
  jackpotItems: JackpotTickerItem[]
}

/** 后端未下发滚动速度时的默认像素/秒 */
export const JACKPOT_SCROLL_SPEED_DEFAULT = 40

/** 后端未下发或非法 empty_mode 时的默认空态 */
export const JACKPOT_EMPTY_MODE_DEFAULT: HomeApiJackpotEmptyMode = 'placeholder'

export const HOME_AGGREGATE_DEFAULTS = {
  sectionLimit: 10,
} as const

const FALLBACK_BANNER_CTA = '立即查看'

/** 热门分类的 code：后端下发该分类时才展示二级 pill（推荐/收藏/最近） */
const HOT_CATEGORY_CODE = 'hot'

const toSource = (code?: string): HomeCategorySource =>
  code === HOT_CATEGORY_CODE ? 'hot' : 'category'

/** display_mode → 是否展示二级厂商侧栏；未知值按 flat 处理 */
const toDisplayMode = (value?: string): HomeCategoryDisplayMode =>
  value === 'vendor' ? 'vendor' : 'flat'

/** grid_columns → 列数；后端只下发 2/3，其余按 3 */
const toColumns = (value?: number): HomeCategoryColumns => (value === 2 ? 2 : 3)

const toEmptyMode = (value?: string): HomeApiJackpotEmptyMode =>
  value === 'hide' ? 'hide' : JACKPOT_EMPTY_MODE_DEFAULT

export function mapHomeData(input: HomeApiData): HomeAggregateViewModel {
  const banners: HomeBannerItem[] = [...(input.banners ?? [])]
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map(item => ({
      id: String(item.id),
      tag: item.tag ?? '',
      title: item.title ?? '',
      subtitle: item.subtitle || undefined,
      ctaText: item.cta_text ?? FALLBACK_BANNER_CTA,
      image: (item.image_url ?? '').trim(),
      imageKey: (item.image_key ?? '').trim() || undefined,
      linkType: normalizeBannerLinkType(item.link_type),
      linkTarget: item.link_target || undefined,
    }))

  const categories: HomeCategoryTab[] = [...(input.categories ?? [])]
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map(item => {
      const iconKey = (item.icon_key ?? '').trim()
      return {
        id: item.code || String(item.id),
        label: item.display_name || item.code || '分类',
        iconUrl: (item.icon_url ?? '').trim(),
        iconKey,
        source: toSource(item.code),
        displayMode: toDisplayMode(item.display_mode),
        columns: toColumns(item.grid_columns),
        vendors: (item.vendors ?? []).map(mapApiVendorToSubTab),
      }
    })

  const recommendedGames: GameItem[] = (input.recommended_games ?? []).map(mapApiGameToItem)

  const jackpotConfig = input.jackpot_config
  const enabled = Boolean(jackpotConfig?.enabled)
  const showAvatar = jackpotConfig?.show_avatar !== false
  const rawSpeed = jackpotConfig?.scroll_speed_pixels_per_second
  const scrollSpeedPixelsPerSecond =
    typeof rawSpeed === 'number' && Number.isFinite(rawSpeed) && rawSpeed > 0
      ? rawSpeed
      : JACKPOT_SCROLL_SPEED_DEFAULT
  const emptyMode = toEmptyMode(jackpotConfig?.empty_mode)
  const emptyText = (jackpotConfig?.empty_text ?? '').trim()
  const jackpotItems = mapJackpotItems(input.jackpot_items ?? [])

  return {
    banners,
    categories,
    recommendedGames,
    enabled,
    showAvatar,
    scrollSpeedPixelsPerSecond,
    emptyMode,
    emptyText,
    jackpotItems,
  }
}

async function fetchHomeAggregate(
  options?: UseHomeAggregateOptions,
): Promise<HomeAggregateViewModel> {
  const merged = { ...HOME_AGGREGATE_DEFAULTS, ...options }
  const data = await fetchHome({
    section_limit: merged.sectionLimit,
  })
  return mapHomeData(data ?? {})
}

/** 首页聚合：一次请求，供轮播/分类/大奖/网格/侧栏拆分复用 */
export function useHomeAggregate(options?: UseHomeAggregateOptions) {
  const sectionLimit = options?.sectionLimit ?? HOME_AGGREGATE_DEFAULTS.sectionLimit
  return useQuery({
    queryKey: ['home', 'aggregate', sectionLimit],
    queryFn: () => fetchHomeAggregate({ sectionLimit }),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}
